<script setup lang="ts">
// Historique des clubs, comités et ligues, saison par saison (cf. backend
// clubs/history_routes.py). Les saisons passées se corrigent ici ; la saison en cours est
// le reflet des écrans Clubs/Comités/Ligues, seul le marquage « disparu » d'un club s'y fait.
// Aussi : dissolution d'un comité ou d'une ligue (dernière saison), création d'un club ou
// d'un comité disparu avec ses seules fiches passées.
import { ref, computed, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';
import ConfirmModal from '@/components/ConfirmModal.vue';
import { useConfirm } from '@/composables/useConfirm';
import { normalizeForSearch } from '@/functions/utils';

const { open: openConfirm } = useConfirm();

interface LigueEntry {
  id: number; ligue_id: number; code: string; name: string; current_code: string | null; last_season: string | null;
}
interface ComiteEntry {
  id: number; comite_id: number; code: string; name: string; ligue_id: number;
  ligue_code: string | null; federal_number: string | null; current_code: string | null; last_season: string | null;
}
interface ClubEntry {
  id: number; club_id: number; number: number; name: string; short_code: string | null;
  comite_id: number | null; comite_code: string | null; affiliated: boolean | null;
  source: string | null; dissolved: boolean; current_name: string | null; current_number: number | null;
}
interface Option { id: number; label: string; }
// Vue « toutes saisons » : une ligne par entité, ses fiches dans l'ordre des saisons.
interface Fiche {
  season: string; number?: number; name: string; code?: string; short_code?: string | null;
  comite_code?: string | null; dissolved?: boolean; source?: string | null;
}
interface AllRow {
  id: number; code: string | null; number: number | null; name: string | null; short_code?: string | null;
  comite_code: string | null; last_season: string | null; fiches: Fiche[];
}
const ALL = 'all';
type Tab = 'clubs' | 'comites' | 'ligues';

const SOURCE_LABELS: Record<string, string> = {
  courante: 'Saison en cours',
  ffd_2025_2026: 'Fichier FFD 2025-2026',
  licences: 'Licences de la saison',
  admin: 'Saisie admin',
  documents: "Documents d'époque",
  deduit: 'Déduit (entre deux saisons attestées)',
};

const seasons = ref<string[]>([]);
const currentSeason = ref('');
const season = ref('');
const tab = ref<Tab>('clubs');
const search = ref('');
const loading = ref(false);
const ligues = ref<LigueEntry[]>([]);
const comites = ref<ComiteEntry[]>([]);
const clubs = ref<ClubEntry[]>([]);
const isCurrent = computed(() => season.value === currentSeason.value);
const isAll = computed(() => season.value === ALL);
const allRows = ref<Record<Tab, AllRow[]>>({ clubs: [], comites: [], ligues: [] });

const filteredAll = computed(() => {
  const q = normalizeForSearch(search.value);
  return allRows.value[tab.value]
    // Nom, numéro, sigle, code (comité/ligue) et comité, actuels et de chaque saison.
    .filter(r => !q || normalizeForSearch(
      `${r.name || ''} ${r.code || ''} ${r.number ?? ''} ${r.short_code || ''} ${r.comite_code || ''} ${r.fiches.map(
        f => `${f.name} ${f.code || ''} ${f.number ?? ''} ${f.short_code || ''} ${f.comite_code || ''}`,
      ).join(' ')}`,
    ).includes(q))
    .sort((a, b) => (a.number ?? 0) - (b.number ?? 0) || (a.code || a.name || '').localeCompare(b.code || b.name || '', 'fr'));
});

// Ce qui a changé au fil des saisons (numéro, nom ou code d'époque différent de l'actuel).
function pastIdentities(r: AllRow): string[] {
  const seen = new Set<string>();
  for (const f of r.fiches) {
    const label = tab.value === 'clubs' ? `${f.number} ${f.name}` : `${f.code} ${f.name}`;
    const now = tab.value === 'clubs' ? `${r.number} ${r.name}` : `${r.code} ${r.name}`;
    if (label !== now) seen.add(label);
  }
  return [...seen];
}

const shortSeason = (s: string) => `${s.slice(2, 4)}-${s.slice(7, 9)}`;

const openSeason = async (s: string) => {
  season.value = s;
  await loadSeason();
};

// Listes complètes (état actuel) pour ajouter une entité à une saison passée.
const allClubs = ref<Option[]>([]);
const allComites = ref<Option[]>([]);
const allLigues = ref<Option[]>([]);

const editing = ref<{ tab: Tab; id: number; form: Record<string, any> } | null>(null);
const adding = ref<{ tab: Tab; entityId: number | null } | null>(null);
const saving = ref(false);

// Dissolution d'un comité ou d'une ligue : dernière saison d'activité (null = actif).
const dissolving = ref<{ tab: 'comites' | 'ligues'; entityId: number; label: string; lastSeason: string | null } | null>(null);
// Création d'un club ou d'un comité qui n'existe plus (fiches des saisons passées uniquement).
const creatingClub = ref<{ number: number | null; name: string; short_code: string; comite_id: number | null; seasons: string[] } | null>(null);
const creatingComite = ref<{ code: string; name: string; ligue_id: number | null; federal_number: string; last_season: string } | null>(null);
const pastSeasons = computed(() => seasons.value.filter(s => s < currentSeason.value));

const filteredClubs = computed(() => {
  const q = normalizeForSearch(search.value);
  return clubs.value.filter(c => !q || normalizeForSearch(
    `${c.name} ${c.short_code || ''} ${c.number} ${c.comite_code || ''} ${c.current_name || ''}`,
  ).includes(q));
});
const filteredComites = computed(() => {
  const q = normalizeForSearch(search.value);
  return comites.value.filter(c => !q || normalizeForSearch(`${c.code} ${c.name} ${c.ligue_code || ''}`).includes(q));
});
const filteredLigues = computed(() => {
  const q = normalizeForSearch(search.value);
  return ligues.value.filter(l => !q || normalizeForSearch(`${l.code} ${l.name}`).includes(q));
});

// Comités et ligues proposés dans les formulaires : ceux de la saison affichée.
const comiteChoices = computed(() => comites.value.map(c => ({ id: c.comite_id, label: `${c.code} · ${c.name}` })));
const ligueChoices = computed(() => ligues.value.map(l => ({ id: l.ligue_id, label: `${l.code} · ${l.name}` })));

const addChoices = computed<Option[]>(() => {
  if (!adding.value) return [];
  if (adding.value.tab === 'clubs') {
    const present = new Set(clubs.value.map(c => c.club_id));
    return allClubs.value.filter(o => !present.has(o.id));
  }
  if (adding.value.tab === 'comites') {
    const present = new Set(comites.value.map(c => c.comite_id));
    return allComites.value.filter(o => !present.has(o.id));
  }
  const present = new Set(ligues.value.map(l => l.ligue_id));
  return allLigues.value.filter(o => !present.has(o.id));
});

function affiliationLabel(c: ClubEntry): { label: string; cls: string } {
  if (c.dissolved) return { label: 'Disparu', cls: 'badge-red' };
  if (c.affiliated === true) return { label: 'Affilié', cls: 'badge-green' };
  if (c.affiliated === false) return { label: 'Non affilié', cls: 'badge-red' };
  return { label: 'Inconnu', cls: 'badge-gray' };
}

const loadSeason = async () => {
  if (!season.value) return;
  loading.value = true;
  try {
    if (isAll.value) {
      const { data } = await dartsHubApi.get('/org-history/all');
      allRows.value = {
        clubs: data.clubs.map((c: any) => ({ ...c, id: c.club_id, code: null, last_season: null })),
        comites: data.comites.map((c: any) => ({ ...c, id: c.comite_id, number: null, comite_code: null })),
        ligues: data.ligues.map((l: any) => ({ ...l, id: l.ligue_id, number: null, comite_code: null })),
      };
      return;
    }
    const { data } = await dartsHubApi.get('/org-history', { params: { season: season.value } });
    ligues.value = data.ligues;
    comites.value = data.comites;
    clubs.value = data.clubs;
  } catch {
    toast.error('Erreur de chargement de la saison.');
  } finally {
    loading.value = false;
  }
};

const loadAll = async () => {
  try {
    const [seasonsRes, clubsRes, comitesRes, liguesRes] = await Promise.all([
      dartsHubApi.get('/org-history/seasons'),
      dartsHubApi.get('/clubs/', { params: { all_comites: true, include_dissolved: true } }),
      dartsHubApi.get('/clubs/comites', { params: { include_dissolved: true } }),
      dartsHubApi.get('/clubs/ligues', { params: { include_dissolved: true } }),
    ]);
    seasons.value = seasonsRes.data.seasons;
    currentSeason.value = seasonsRes.data.current;
    season.value = season.value || seasonsRes.data.current;
    allClubs.value = clubsRes.data
      .map((c: any) => ({ id: c.id, label: `${c.name} (${c.number ?? '?'})` }))
      .sort((a: Option, b: Option) => a.label.localeCompare(b.label, 'fr'));
    allComites.value = comitesRes.data.map((c: any) => ({ id: c.id, label: `${c.code} · ${c.name}` }));
    allLigues.value = liguesRes.data.map((l: any) => ({ id: l.id, label: `${l.code} · ${l.name}` }));
  } catch {
    toast.error('Erreur de chargement.');
  }
  await loadSeason();
};

const openEdit = (t: Tab, entry: any) => {
  const fields: Record<Tab, string[]> = {
    clubs: ['number', 'name', 'short_code', 'comite_id', 'affiliated'],
    comites: ['code', 'name', 'ligue_id', 'federal_number'],
    ligues: ['code', 'name'],
  };
  editing.value = { tab: t, id: entry.id, form: Object.fromEntries(fields[t].map(f => [f, entry[f] ?? null])) };
};

const saveEdit = async () => {
  if (!editing.value) return;
  saving.value = true;
  try {
    const form = { ...editing.value.form };
    if (editing.value.tab === 'clubs') form.number = Number(form.number);
    await dartsHubApi.put(`/org-history/${editing.value.tab}/entries/${editing.value.id}`, form);
    toast.success('Fiche enregistrée.');
    editing.value = null;
    await loadSeason();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement.");
  } finally {
    saving.value = false;
  }
};

const saveAdd = async () => {
  if (!adding.value?.entityId) return;
  saving.value = true;
  try {
    await dartsHubApi.post(`/org-history/${adding.value.tab}/entries`, { season: season.value, entity_id: adding.value.entityId });
    toast.success('Ajouté à la saison : vérifiez et corrigez la fiche (pré-remplie avec l\'état actuel).');
    adding.value = null;
    await loadSeason();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || "Erreur lors de l'ajout.");
  } finally {
    saving.value = false;
  }
};

const remove = async (t: Tab, entry: any, label: string) => {
  const message = t === 'clubs' && isCurrent.value
    ? `Marquer « ${label} » comme disparu pour la saison ${season.value} ? Réversible.`
    : `Retirer « ${label} » de la saison ${season.value} (il n'existait pas, ou plus) ?`;
  if (!(await openConfirm(t === 'clubs' && isCurrent.value ? 'Club disparu' : 'Retirer de la saison', message))) return;
  try {
    await dartsHubApi.delete(`/org-history/${t}/entries/${entry.id}`);
    await loadSeason();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de la suppression.');
  }
};

const openDissolve = (t: 'comites' | 'ligues', entry: ComiteEntry | LigueEntry) => {
  const entityId = t === 'comites' ? (entry as ComiteEntry).comite_id : (entry as LigueEntry).ligue_id;
  dissolving.value = { tab: t, entityId, label: `${entry.code} · ${entry.name}`, lastSeason: entry.last_season };
};

const saveDissolve = async () => {
  if (!dissolving.value) return;
  saving.value = true;
  try {
    const { tab: t, entityId, lastSeason } = dissolving.value;
    await dartsHubApi.put(`/org-history/${t}/${entityId}/last-season`, { last_season: lastSeason });
    toast.success(lastSeason ? `Dissous après ${lastSeason}.` : 'De nouveau actif.');
    dissolving.value = null;
    await loadAll();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement.");
  } finally {
    saving.value = false;
  }
};

const openCreateClub = () => {
  creatingClub.value = { number: null, name: '', short_code: '', comite_id: null, seasons: [season.value] };
};

const saveCreateClub = async () => {
  const f = creatingClub.value;
  if (!f || !f.number || !f.name.trim() || !f.comite_id || !f.seasons.length) return;
  saving.value = true;
  try {
    const { data } = await dartsHubApi.post('/org-history/clubs/historical', { ...f, number: Number(f.number) });
    toast.success(data.number_reused
      ? 'Club disparu créé. Son ancien numéro a été réattribué depuis : il porte un numéro provisoire hors historique.'
      : 'Club disparu créé.');
    creatingClub.value = null;
    await loadAll();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de la création.');
  } finally {
    saving.value = false;
  }
};

const openCreateComite = () => {
  creatingComite.value = { code: '', name: '', ligue_id: null, federal_number: '', last_season: season.value };
};

const saveCreateComite = async () => {
  const f = creatingComite.value;
  if (!f || !f.code.trim() || !f.name.trim() || !f.ligue_id || !f.last_season) return;
  saving.value = true;
  try {
    await dartsHubApi.post('/org-history/comites/historical', { ...f, federal_number: f.federal_number || null });
    toast.success('Comité disparu créé : ajoutez-le maintenant aux saisons où il existait.');
    creatingComite.value = null;
    await loadAll();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur lors de la création.');
  } finally {
    saving.value = false;
  }
};

const restore = async (entry: ClubEntry) => {
  try {
    await dartsHubApi.post(`/org-history/clubs/entries/${entry.id}/restore`);
    await loadSeason();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  }
};

onMounted(loadAll);
</script>

<template>
  <div class="page">
    <ConfirmModal />
    <div class="page-header">
      <h1><i class="fa-solid fa-clock-rotate-left" /> Historique clubs, comités, ligues</h1>
      <div class="filters">
        <select v-model="season" class="season-select" @change="loadSeason">
          <option :value="ALL">Toutes saisons</option>
          <option v-for="s in seasons" :value="s" :key="s">{{ s }}{{ s === currentSeason ? ' (en cours)' : '' }}</option>
        </select>
        <div class="search-bar">
          <i class="fa-solid fa-magnifying-glass" style="color:var(--text-muted)" />
          <input v-model="search" placeholder="Nom, sigle, numéro, code…" />
        </div>
      </div>
    </div>

    <p class="page-hint">
      <template v-if="isAll">
        Toutes saisons : chaque club, comité ou ligue une seule fois, avec les saisons où il a une fiche (en rouge : marqué
        disparu ; en pointillés : saison déduite, sans document, entre deux saisons attestées) et ses numéros, noms ou
        codes d'époque. Cliquez sur une saison pour l'ouvrir et la corriger.
      </template>
      <template v-else-if="isCurrent">
        Saison en cours : reflet des écrans Clubs, Comités et Ligues, mis à jour chaque nuit. Modifiez-les là-bas ;
        ici, seul le marquage d'un club disparu est possible.
      </template>
      <template v-else>
        Saison passée : fiches figées, telles que reconstituées (fichier FFD, licences) ou saisies ici. Vous pouvez les
        corriger, en ajouter ou en retirer.
      </template>
    </p>

    <div class="tabs">
      <button class="tab" :class="{ active: tab === 'clubs' }" @click="tab = 'clubs'">Clubs ({{ isAll ? allRows.clubs.length : clubs.length }})</button>
      <button class="tab" :class="{ active: tab === 'comites' }" @click="tab = 'comites'">Comités ({{ isAll ? allRows.comites.length : comites.length }})</button>
      <button class="tab" :class="{ active: tab === 'ligues' }" @click="tab = 'ligues'">Ligues ({{ isAll ? allRows.ligues.length : ligues.length }})</button>
      <template v-if="!isCurrent && !isAll">
        <button class="btn btn-primary btn-sm add-btn" @click="adding = { tab, entityId: null }">
          <i class="fa-solid fa-plus" /> Ajouter à {{ season }}
        </button>
        <button v-if="tab === 'clubs'" class="btn btn-ghost btn-sm" @click="openCreateClub">
          <i class="fa-solid fa-ghost" /> Club disparu…
        </button>
        <button v-else-if="tab === 'comites'" class="btn btn-ghost btn-sm" @click="openCreateComite">
          <i class="fa-solid fa-ghost" /> Comité disparu…
        </button>
      </template>
    </div>

    <div v-if="loading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>

    <div v-else-if="isAll" class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>{{ tab === 'clubs' ? 'N° actuel' : 'Code' }}</th><th>Nom actuel</th><th v-if="tab === 'clubs'">Comité</th>
            <th>Saisons</th><th>Autrefois</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in filteredAll" :key="r.id">
            <td class="cell-num">
              <template v-if="tab === 'clubs'">{{ r.number }}</template>
              <span v-else class="badge badge-gray">{{ r.code }}</span>
            </td>
            <td>
              <span class="club-name">{{ r.name }}</span>
              <span v-if="r.short_code" class="cell-muted"> · {{ r.short_code }}</span>
              <span v-if="r.last_season" class="badge badge-red">dissous après {{ r.last_season }}</span>
            </td>
            <td v-if="tab === 'clubs'"><span class="badge badge-gray">{{ r.comite_code || '-' }}</span></td>
            <td>
              <div class="season-chips">
                <button v-for="f in r.fiches" :key="f.season" class="badge season-chip"
                  :class="[f.dissolved ? 'badge-red' : 'badge-gray', { deduced: f.source === 'deduit' }]"
                  :title="(tab === 'clubs' ? `${f.season} : ${f.number} ${f.name} (${f.comite_code || '-'})` : `${f.season} : ${f.code} ${f.name}`)
                    + (f.dissolved ? ' · disparu' : '') + (f.source === 'deduit' ? ' · déduite (entre deux saisons attestées)' : '')"
                  @click="openSeason(f.season)">{{ shortSeason(f.season) }}</button>
              </div>
            </td>
            <td class="cell-muted">
              <div v-for="p in pastIdentities(r)" :key="p">{{ p }}</div>
            </td>
          </tr>
          <tr v-if="!filteredAll.length"><td :colspan="tab === 'clubs' ? 5 : 4" class="empty">Aucune fiche</td></tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="tab === 'clubs'" class="table-wrapper">
      <table>
        <thead>
          <tr><th>N°</th><th>Nom</th><th>Sigle</th><th>Comité</th><th>Statut</th><th>Source</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="c in filteredClubs" :class="{ dissolved: c.dissolved }" :key="c.id">
            <td class="cell-num">
              {{ c.number }}
              <div v-if="c.current_number !== null && c.current_number !== c.number" class="now">auj. {{ c.current_number }}</div>
            </td>
            <td>
              <span class="club-name">{{ c.name }}</span>
              <div v-if="c.current_name && c.current_name !== c.name" class="now">auj. {{ c.current_name }}</div>
            </td>
            <td>{{ c.short_code || '' }}</td>
            <td><span class="badge badge-gray">{{ c.comite_code || '-' }}</span></td>
            <td><span class="badge" :class="affiliationLabel(c).cls">{{ affiliationLabel(c).label }}</span></td>
            <td class="cell-muted">{{ SOURCE_LABELS[c.source || ''] || c.source || '' }}</td>
            <td class="actions">
              <button v-if="!isCurrent" class="btn btn-ghost btn-sm" title="Corriger" @click="openEdit('clubs', c)"><i class="fa-solid fa-pen" /></button>
              <button v-if="c.dissolved" class="btn btn-ghost btn-sm" title="Annuler « disparu »" @click="restore(c)"><i class="fa-solid fa-rotate-left" /></button>
              <button v-else class="btn btn-ghost btn-sm" :title="isCurrent ? 'Marquer disparu' : 'Retirer de la saison'" @click="remove('clubs', c, c.name)"><i class="fa-solid fa-trash" /></button>
            </td>
          </tr>
          <tr v-if="!filteredClubs.length"><td colspan="7" class="empty">Aucun club sur cette saison</td></tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="tab === 'comites'" class="table-wrapper">
      <table>
        <thead><tr><th>Code</th><th>Nom</th><th>Ligue</th><th>N° fédéral</th><th></th></tr></thead>
        <tbody>
          <tr v-for="c in filteredComites" :key="c.id">
            <td>
              <span class="badge badge-gray">{{ c.code }}</span>
              <div v-if="c.current_code && c.current_code !== c.code" class="now">auj. {{ c.current_code }}</div>
            </td>
            <td class="club-name">
              {{ c.name }}
              <span v-if="c.last_season" class="badge badge-red">dissous après {{ c.last_season }}</span>
            </td>
            <td><span class="badge badge-gray">{{ c.ligue_code || '-' }}</span></td>
            <td class="cell-muted">{{ c.federal_number || '' }}</td>
            <td class="actions">
              <template v-if="!isCurrent">
                <button class="btn btn-ghost btn-sm" title="Corriger" @click="openEdit('comites', c)"><i class="fa-solid fa-pen" /></button>
                <button class="btn btn-ghost btn-sm" title="Retirer de la saison" @click="remove('comites', c, c.name)"><i class="fa-solid fa-trash" /></button>
              </template>
              <button class="btn btn-ghost btn-sm" title="Dissolution" @click="openDissolve('comites', c)"><i class="fa-solid fa-box-archive" /></button>
            </td>
          </tr>
          <tr v-if="!filteredComites.length"><td colspan="5" class="empty">Aucun comité sur cette saison</td></tr>
        </tbody>
      </table>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead><tr><th>Code</th><th>Nom</th><th></th></tr></thead>
        <tbody>
          <tr v-for="l in filteredLigues" :key="l.id">
            <td>
              <span class="badge badge-gray">{{ l.code }}</span>
              <div v-if="l.current_code && l.current_code !== l.code" class="now">auj. {{ l.current_code }}</div>
            </td>
            <td class="club-name">
              {{ l.name }}
              <span v-if="l.last_season" class="badge badge-red">dissoute après {{ l.last_season }}</span>
            </td>
            <td class="actions">
              <template v-if="!isCurrent">
                <button class="btn btn-ghost btn-sm" title="Corriger" @click="openEdit('ligues', l)"><i class="fa-solid fa-pen" /></button>
                <button class="btn btn-ghost btn-sm" title="Retirer de la saison" @click="remove('ligues', l, l.name)"><i class="fa-solid fa-trash" /></button>
              </template>
              <button class="btn btn-ghost btn-sm" title="Dissolution" @click="openDissolve('ligues', l)"><i class="fa-solid fa-box-archive" /></button>
            </td>
          </tr>
          <tr v-if="!filteredLigues.length"><td colspan="3" class="empty">Aucune ligue sur cette saison</td></tr>
        </tbody>
      </table>
    </div>

    <!-- Correction d'une fiche (saison passée) -->
    <div v-if="editing" class="modal-overlay" @click.self="editing = null">
      <div class="modal">
        <div class="modal-header">
          <h2>Corriger la fiche {{ season }}</h2>
          <button class="btn btn-ghost btn-sm" @click="editing = null"><i class="fa-solid fa-xmark" /></button>
        </div>
        <template v-if="editing.tab === 'clubs'">
          <div class="form-group"><label>N° de club</label><input v-model="editing.form.number" type="number" /></div>
          <div class="form-group"><label>Nom à l'époque</label><input v-model="editing.form.name" type="text" /></div>
          <div class="form-group"><label>Sigle</label><input v-model="editing.form.short_code" type="text" /></div>
          <div class="form-group">
            <label>Comité</label>
            <select v-model="editing.form.comite_id">
              <option :value="null">-</option>
              <option v-for="o in comiteChoices" :value="o.id" :key="o.id">{{ o.label }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Affiliation</label>
            <select v-model="editing.form.affiliated">
              <option :value="true">Affilié</option>
              <option :value="false">Non affilié</option>
              <option :value="null">Inconnu</option>
            </select>
          </div>
        </template>
        <template v-else-if="editing.tab === 'comites'">
          <div class="form-group"><label>Code</label><input v-model="editing.form.code" type="text" /></div>
          <div class="form-group"><label>Nom</label><input v-model="editing.form.name" type="text" /></div>
          <div class="form-group">
            <label>Ligue</label>
            <select v-model="editing.form.ligue_id">
              <option v-for="o in ligueChoices" :value="o.id" :key="o.id">{{ o.label }}</option>
            </select>
          </div>
          <div class="form-group"><label>N° fédéral</label><input v-model="editing.form.federal_number" type="text" /></div>
        </template>
        <template v-else>
          <div class="form-group"><label>Code</label><input v-model="editing.form.code" type="text" /></div>
          <div class="form-group"><label>Nom</label><input v-model="editing.form.name" type="text" /></div>
        </template>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="editing = null">Annuler</button>
          <button class="btn btn-primary btn-sm" :disabled="saving" @click="saveEdit">
            <i v-if="saving" class="fa-solid fa-spinner fa-spin" /> Enregistrer
          </button>
        </div>
      </div>
    </div>

    <!-- Ajout d'un club/comité/ligue à une saison passée -->
    <div v-if="adding" class="modal-overlay" @click.self="adding = null">
      <div class="modal">
        <div class="modal-header">
          <h2>Ajouter à la saison {{ season }}</h2>
          <button class="btn btn-ghost btn-sm" @click="adding = null"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-group">
          <label>{{ adding.tab === 'clubs' ? 'Club' : adding.tab === 'comites' ? 'Comité' : 'Ligue' }}</label>
          <select v-model="adding.entityId">
            <option :value="null" disabled>Choisir…</option>
            <option v-for="o in addChoices" :value="o.id" :key="o.id">{{ o.label }}</option>
          </select>
          <p class="page-hint">La fiche est pré-remplie avec l'état actuel : corrigez-la ensuite (numéro, nom, comité de l'époque).</p>
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="adding = null">Annuler</button>
          <button class="btn btn-primary btn-sm" :disabled="saving || !adding.entityId" @click="saveAdd">Ajouter</button>
        </div>
      </div>
    </div>

    <!-- Dissolution d'un comité ou d'une ligue -->
    <div v-if="dissolving" class="modal-overlay" @click.self="dissolving = null">
      <div class="modal">
        <div class="modal-header">
          <h2>Dissolution · {{ dissolving.label }}</h2>
          <button class="btn btn-ghost btn-sm" @click="dissolving = null"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-group">
          <label>Dernière saison d'activité</label>
          <select v-model="dissolving.lastSeason">
            <option :value="null">Toujours actif</option>
            <option v-for="s in seasons" :value="s" :key="s">{{ s }}</option>
          </select>
          <p class="page-hint">
            Au-delà, plus de fiche sur la saison en cours et plus de place dans les listes de choix. Ses fiches passées,
            licences et événements restent. Une ligue ne se dissout qu'une fois tous ses comités dissous.
          </p>
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="dissolving = null">Annuler</button>
          <button class="btn btn-primary btn-sm" :disabled="saving" @click="saveDissolve">Enregistrer</button>
        </div>
      </div>
    </div>

    <!-- Club disparu : créé avec ses fiches des saisons passées -->
    <div v-if="creatingClub" class="modal-overlay" @click.self="creatingClub = null">
      <div class="modal">
        <div class="modal-header">
          <h2>Club disparu</h2>
          <button class="btn btn-ghost btn-sm" @click="creatingClub = null"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-group"><label>N° de club à l'époque</label><input v-model="creatingClub.number" type="number" placeholder="28002000" /></div>
        <div class="form-group"><label>Nom</label><input v-model="creatingClub.name" type="text" /></div>
        <div class="form-group"><label>Sigle</label><input v-model="creatingClub.short_code" type="text" /></div>
        <div class="form-group">
          <label>Comité ({{ season }})</label>
          <select v-model="creatingClub.comite_id">
            <option :value="null" disabled>Choisir…</option>
            <option v-for="o in comiteChoices" :value="o.id" :key="o.id">{{ o.label }}</option>
          </select>
        </div>
        <div class="form-group">
          <label>Saisons où il existait</label>
          <div class="season-checks">
            <label v-for="s in pastSeasons" :key="s" class="season-check">
              <input v-model="creatingClub.seasons" type="checkbox" :value="s" /> {{ s }}
            </label>
          </div>
          <p class="page-hint">Même numéro, nom et comité sur chaque saison cochée : corrigez ensuite une fiche si besoin.</p>
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="creatingClub = null">Annuler</button>
          <button class="btn btn-primary btn-sm"
            :disabled="saving || !creatingClub.number || !creatingClub.name.trim() || !creatingClub.comite_id || !creatingClub.seasons.length"
            @click="saveCreateClub">Créer</button>
        </div>
      </div>
    </div>

    <!-- Comité disparu (ex. AQU) : n'apparaît que dans l'historique -->
    <div v-if="creatingComite" class="modal-overlay" @click.self="creatingComite = null">
      <div class="modal">
        <div class="modal-header">
          <h2>Comité disparu</h2>
          <button class="btn btn-ghost btn-sm" @click="creatingComite = null"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-group"><label>Code</label><input v-model="creatingComite.code" type="text" placeholder="AQU" /></div>
        <div class="form-group"><label>Nom</label><input v-model="creatingComite.name" type="text" /></div>
        <div class="form-group">
          <label>Ligue</label>
          <select v-model="creatingComite.ligue_id">
            <option :value="null" disabled>Choisir…</option>
            <option v-for="o in allLigues" :value="o.id" :key="o.id">{{ o.label }}</option>
          </select>
        </div>
        <div class="form-group"><label>N° fédéral</label><input v-model="creatingComite.federal_number" type="text" placeholder="32000" /></div>
        <div class="form-group">
          <label>Dernière saison d'activité</label>
          <select v-model="creatingComite.last_season">
            <option v-for="s in pastSeasons" :value="s" :key="s">{{ s }}</option>
          </select>
          <p class="page-hint">Ajoutez-le ensuite aux saisons où il existait (onglet Comités, « Ajouter à… »).</p>
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="creatingComite = null">Annuler</button>
          <button class="btn btn-primary btn-sm"
            :disabled="saving || !creatingComite.code.trim() || !creatingComite.name.trim() || !creatingComite.ligue_id"
            @click="saveCreateComite">Créer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filters {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.season-select {
  padding: 0.4rem 0.6rem;
}

.page-hint {
  color: var(--text-muted);
  font-size: 0.85rem;
  margin: -0.5rem 0 1rem;
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 1rem;
}

.tab {
  padding: 0.35rem 0.8rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--bg-card);
  color: inherit;
  cursor: pointer;
  font-size: 0.85rem;
}

.tab.active {
  border-color: var(--text-muted);
  font-weight: 600;
}

.add-btn {
  margin-left: auto;
}

.season-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.season-chip {
  border: 1px solid transparent;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
}

/* Saison déduite (aucun document, entre deux saisons attestées) */
.season-chip.deduced {
  background: transparent;
  border: 1px dashed var(--text-muted);
  font-style: italic;
}

.season-checks {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(7.5rem, 1fr));
  gap: 0.25rem 0.75rem;
}

.season-check {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  font-weight: normal;
}

.club-name {
  font-weight: 500;
}

.cell-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.cell-muted {
  color: var(--text-muted);
  font-size: 0.8rem;
}

.now {
  color: var(--text-muted);
  font-size: 0.75rem;
}

.actions {
  white-space: nowrap;
  text-align: right;
}

.empty {
  text-align: center;
  color: var(--text-muted);
}

tr.dissolved td {
  opacity: 0.6;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 1rem;
  overflow-y: auto;
}

.modal {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  max-width: 480px;
  width: 100%;
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  margin: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.modal-header h2 {
  font-size: 1rem;
  font-weight: 600;
}
</style>
